import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Layout, Card, FormLayout, TextField, Modal, Popover, ActionList, DataTable, Select, Stack, Tag, Button, Spinner, Banner } from '@shopify/polaris'

import { useAdmin } from 'context/admin';
import { MoreHoriz as MoreIcon } from '@material-ui/icons';
import { postData, putData, fetcher, handleErrors } from 'util/fetch';
import { validEmail } from 'util/helpers';

export default function OrderSummaryEmailSettings() {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [popover, setPopover] = useState(false);
  const [orderSummaryEmails, setOrderSummaryEmails] = useState([]);
  const { selectedShop } = useAdmin();
  const [newRecipientAddress, setNewRecipientAddress] = useState('');
  const [newRecipientValue, setNewRecipientValue] = useState('');
  const [previewModal, setPreviewModal] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const { setToast } = useAdmin();

  const normalizeRecipient = useCallback((recipient = {}) => ({
    ...recipient,
    type: recipient.type || 'product_type',
    values: Array.isArray(recipient.values) ? recipient.values : [],
    addresses: Array.isArray(recipient.addresses) ? recipient.addresses : []
  }), []);

  const normalizeSummary = useCallback((summary = {}) => ({
    ...summary,
    recipients: Array.isArray(summary.recipients)
      ? summary.recipients.map(normalizeRecipient)
      : [],
    triggers: Array.isArray(summary.triggers) ? summary.triggers : []
  }), [normalizeRecipient]);

  const recipientTypeLabel = (type) => {
    switch (type) {
      case 'collection':
        return 'Collection';
      case 'product_type':
      default:
        return 'Product Type';
    }
  };

  const recipientOptionLabel = (recipient) => {
    if (!recipient) {
      return '';
    }
    const typeLabel = recipientTypeLabel(recipient.type);
    const values = Array.isArray(recipient.values) ? recipient.values.filter(Boolean) : [];
    if (values.length === 0) {
      return `${typeLabel}: All`;
    }
    return `${typeLabel}: ${values.join(', ')}`;
  };

  const triggerOptionLabel = (trigger) => {
    if (!trigger) {
      return 'Any time';
    }

    const hourValue = trigger.hour;
    const hourNumber = Number(hourValue);
    const hourLabel = Number.isFinite(hourNumber) ? `${hourNumber}:00` : (hourValue ? `${hourValue}` : 'Scheduled');

    if (trigger.type === 'hours_ago') {
      const duration = Number(trigger.value);
      const durationLabel = Number.isFinite(duration) ? duration : trigger.value;
      return `${hourLabel} • last ${durationLabel}h`;
    }

    if (trigger.type === 'date') {
      const valueLabel = trigger.value === 'next_day' ? 'Next day due' : trigger.value;
      return `${hourLabel} • ${valueLabel}`;
    }

    return hourLabel;
  };

  const newTrigger = useMemo(() => ({ hour: 17, type: 'date', value: 'next_day' }), []);
  const createNewRecipient = useCallback(
    () => normalizeRecipient({ type: 'product_type', values: [], addresses: [] }),
    [normalizeRecipient]
  );

  useEffect(() => {
    if (selectedShop && !loaded && !loading) {
      setLoading(true);
      fetcher(`/api/shops/${selectedShop.id}/order_summary_emails`)
        .then(res => res.json())
        .then(res => {
          const summaries = Array.isArray(res) ? res : [];
          setOrderSummaryEmails(summaries.map(normalizeSummary));
        })
        .finally(() => {
          setLoading(false);
          setLoaded(true);
        })
    }
  }, [selectedShop, loaded, loading, normalizeSummary]);

  useEffect(() => {
    if (!editing || !editing.recipient) {
      setNewRecipientAddress('');
      setNewRecipientValue('');
    }
  }, [editing]);

  const handleAdd = () => {
    postData(`/api/shops/${selectedShop.id}/order_summary_emails`)
      .then(res => res.json())
      .then(res => {
        setOrderSummaryEmails([normalizeSummary(res)]);
      })
  }

  const handleSave = (summary) => {
    setSaving(true);
    putData(`/api/shops/${selectedShop.id}/order_summary_emails/${summary.id}`, { order_summary_email: summary })
      .then(res => res.json())
      .then(res => {
        const normalized = normalizeSummary(res);
        setOrderSummaryEmails(orderSummaryEmails.map(x => x.id === summary.id ? normalized : x));
        setEditing(false);
        setToast('Order summary email saved.')
      })
      .finally(() => setSaving(false))
  }

  const handleSaveRecipient = ({ recipient, summaryIndex, recipientIndex }) => {
    const summary = orderSummaryEmails[summaryIndex];
    const normalizedRecipient = normalizeRecipient(recipient);
    handleSave({
      ...summary,
      recipients: recipientIndex === undefined
        ? [...summary.recipients, normalizedRecipient]
        : summary.recipients.map((x, i) => i === recipientIndex ? normalizedRecipient : x)
    })
  }

  const handleSaveTrigger = ({ trigger, summaryIndex, triggerIndex }) => {
    const summary = orderSummaryEmails[summaryIndex];
    handleSave({
      ...summary,
      triggers: triggerIndex === undefined
      ? [...summary.triggers, trigger]
      : summary.triggers.map((x, i) => i === triggerIndex ? trigger : x)
    })
  }

  const handleRemoveTrigger = (summaryIndex, index) => {
    const summary = orderSummaryEmails[summaryIndex];
    handleSave({
      ...summary,
      triggers: summary.triggers.filter((r, i) => i !== index)
    })
  }

  const handleRemoveRecipient = (summaryIndex, index) => {
    const summary = orderSummaryEmails[summaryIndex];
    handleSave({
      ...summary,
      recipients: summary.recipients.filter((r, i) => i !== index)
    })
  }

  const loadPreview = (options) => {
    if (!options) {
      return;
    }

    if (!selectedShop?.id) {
      setPreviewError('Select a shop to preview the email.');
      setPreviewHtml('');
      setPreviewLoading(false);
      return;
    }

    const summary = orderSummaryEmails[options.summaryIndex];

    if (!summary) {
      setPreviewError('Unable to find summary configuration.');
      setPreviewHtml('');
      setPreviewLoading(false);
      return;
    }

    const recipientIndex = options.recipientIndex === '' || options.recipientIndex === null || options.recipientIndex === undefined
      ? null
      : Number(options.recipientIndex);

    if (recipientIndex === null || Number.isNaN(recipientIndex)) {
      setPreviewError('Select a recipient to preview the email.');
      setPreviewHtml('');
      setPreviewLoading(false);
      return;
    }

    const recipient = summary.recipients?.[recipientIndex];

    if (!recipient) {
      setPreviewError('Recipient not found.');
      setPreviewHtml('');
      setPreviewLoading(false);
      return;
    }

    setPreviewLoading(true);
    setPreviewError('');
    setPreviewHtml('');

    const params = new URLSearchParams({ recipient_index: recipientIndex });

    const triggerIndex = options.triggerIndex === '' || options.triggerIndex === null || options.triggerIndex === undefined
      ? null
      : Number(options.triggerIndex);

    if (triggerIndex !== null && !Number.isNaN(triggerIndex)) {
      params.set('trigger_index', triggerIndex);
    }

    if (options.date) {
      params.set('date', options.date);
    }

    fetcher(`/api/shops/${selectedShop.id}/order_summary_emails/${summary.id}/preview?${params.toString()}`)
      .then(handleErrors)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          setPreviewError(data.error);
          setPreviewHtml('');
        } else {
          setPreviewHtml(data.html || '');
        }
      })
      .catch(() => {
        setPreviewError('Failed to load preview.');
        setPreviewHtml('');
      })
      .finally(() => setPreviewLoading(false));
  };

  const handleOpenPreview = (summaryIndex) => {
    const summary = orderSummaryEmails[summaryIndex];
    const hasRecipients = summary && Array.isArray(summary.recipients) && summary.recipients.length > 0;
    const hasTriggers = summary && Array.isArray(summary.triggers) && summary.triggers.length > 0;

    const nextState = {
      open: true,
      summaryIndex,
      recipientIndex: hasRecipients ? '0' : '',
      triggerIndex: hasTriggers ? '0' : '',
      date: ''
    };

    setPreviewModal(nextState);
    setPreviewHtml('');
    setPreviewError('');
    setPreviewLoading(false);

    if (hasRecipients) {
      loadPreview(nextState);
    } else {
      setPreviewError('Add a recipient to preview the email.');
    }
  };

  const handleClosePreview = () => {
    setPreviewModal(null);
    setPreviewHtml('');
    setPreviewError('');
    setPreviewLoading(false);
  };

  const handlePreviewRecipientChange = (value) => {
    setPreviewModal((state) => {
      if (!state) {
        return state;
      }
      const nextState = { ...state, recipientIndex: value };
      loadPreview(nextState);
      return nextState;
    });
  };

  const handlePreviewTriggerChange = (value) => {
    setPreviewModal((state) => {
      if (!state) {
        return state;
      }
      const nextState = { ...state, triggerIndex: value };
      loadPreview(nextState);
      return nextState;
    });
  };

  const handlePreviewDateChange = (value) => {
    setPreviewModal((state) => {
      if (!state) {
        return state;
      }
      return { ...state, date: value };
    });
  };

  const triggerHeaders = (summaryIndex) => [
    { type: 'text', label: 'Hour', fn: (x) => `${x.hour}:00` },
    { type: 'text', label: 'Type', fn: (x) => {
      if (x.type === 'date' && x.value === 'next_day') {
        return 'Next Days Orders'
      }
      if (x.type === 'hours_ago') {
        return `Orders created after ${x.hour - x.value}:00`;
      }
    }},
    { type: 'numeric', col: null, label: '', fn: (trigger, index) => {
      const key = `${trigger.hour}-${index}`;
      return <Popover
        active={popover === key}
        activator={<MoreIcon style={{cursor: 'pointer'}} onClick={() => setPopover(key)} />}
        onClose={() => setPopover(false)}
      >
          <ActionList items={[
            {content: 'Edit', onAction: () => setEditing({ trigger, summaryIndex, triggerIndex: index }) },
            {content: 'Delete', onAction: () => handleRemoveTrigger(summaryIndex, index), destructive: true }
          ]} />
      </Popover>
    }}
  ];

  const recipientHeaders = (summaryIndex) => [
    { type: 'text', label: 'Filter Type', fn: (x) => recipientTypeLabel(x?.type) },
    { type: 'text', label: 'Filters', fn: (x) => {
      const values = Array.isArray(x?.values) ? x.values : [];
      if (!values.length) {
        return <div>All {recipientTypeLabel(x?.type)}s</div>;
      }
      return <div>{values.map(value => <div key={value}>{value}</div>)}</div>
    }},
    { type: 'text', label: 'Emails', fn: (x) => {
      const addresses = Array.isArray(x?.addresses) ? x.addresses : [];
      return <div>{addresses.map(address => <div key={address}>{address}</div>)}</div>
    }},
    { type: 'numeric', col: null, label: '', fn: (recipient, index) => {
      const values = Array.isArray(recipient?.values) ? recipient.values : [];
      const addresses = Array.isArray(recipient?.addresses) ? recipient.addresses : [];
      const key = `${recipient?.type || 'any'}__${values.join('-')}__${addresses.join('-')}-${index}`;
      return (
        <Popover
          active={popover === key}
          activator={<MoreIcon style={{cursor: 'pointer'}} onClick={() => setPopover(key)} />}
          onClose={() => setPopover(false)}
        >
          <ActionList items={[
            {content: 'Edit', onAction: () => setEditing({ recipient: normalizeRecipient(recipient), summaryIndex, recipientIndex: index }) },
            {content: 'Delete', onAction: () => handleRemoveRecipient(summaryIndex, index), destructive: true }
          ]} />
        </Popover>
      )
    }}
  ];

  return (
    <Layout.AnnotatedSection
      title="Order Summary Emails"
      description="Get daily emails with order summary based on your department."
    >
        {!loading && orderSummaryEmails.length === 0 ?
          <Card>
            <Card.Header
              title="Summary Emails"
              actions={[{ content: 'Add', onAction: handleAdd }]}
            >
            </Card.Header>
            <Card.Section>
              No order summary emails
            </Card.Section>
          </Card>
        : null}

        {!loading && orderSummaryEmails.length > 0 ?
          orderSummaryEmails.map((summary, index) =>
            <Card key={index}>
              <Card.Section title="Triggers" actions={[{ content: 'Add', onAction: () => setEditing({ trigger: newTrigger, summaryIndex: index, triggerIndex: undefined }) }]}>
                {summary.triggers.length > 0 ?
                  <DataTable
                    columnContentTypes={triggerHeaders(index).map(h => h.type)}
                    headings={triggerHeaders(index).map(h => h.label)}
                    rows={summary.triggers.map((row, idx) => triggerHeaders(index).map(header => header.fn(row, idx)))}
                  />
                : 'Add a trigger'}
              </Card.Section>

              {(() => {
                const recipientActions = [
                  { content: 'Add', onAction: () => setEditing({ recipient: createNewRecipient(), summaryIndex: index, recipientIndex: undefined }) }
                ];

                if ((summary.recipients || []).length > 0) {
                  recipientActions.push({ content: 'Preview', onAction: () => handleOpenPreview(index) });
                }

                return (
                  <Card.Section title="Recipients" actions={recipientActions}>
                    {summary.recipients.length > 0 ?
                      <DataTable
                        columnContentTypes={recipientHeaders(index).map(h => h.type)}
                        headings={recipientHeaders(index).map(h => h.label)}
                        rows={summary.recipients.map((row, idx) => recipientHeaders(index).map(header => header.fn(row, idx)))}
                      />
                    : 'Add recipients'}
                  </Card.Section>
                );
              })()}
            </Card>
          )
        : null}

      <Modal
        title="Trigger Modal"
        open={editing.trigger}
        onClose={() => setEditing(false)}
        secondaryActions={[{ content: 'Cancel', onAction: () => setEditing(false) }]}
        primaryAction={{content: 'Save', loading: saving, onAction: () => handleSaveTrigger(editing), disabled: saving }}>
        {editing.trigger ?
          <Modal.Section>
            {editing.trigger.type === 'hours_ago' ? <p>At {editing.trigger.hour}:00, get a summary of orders created since {editing.trigger.hour - editing.trigger.value}:00.</p> : null}
            {editing.trigger.type === 'date' ? <p>At {editing.trigger.hour}:00, get a summary of orders due to be delivered or picked up {editing.trigger.value === 'next_day' ? 'tomorrow' : editing.trigger.value}.</p> : null}

            {editing.trigger ?
              <FormLayout>
                <TextField
                  label="Hour"
                  type="number"
                  suffix=":00"
                  min={0}
                  max={23}
                  value={`${editing.trigger.hour}`}
                  onChange={(val) => setEditing({ ...editing, trigger: { ...editing.trigger, hour: +val } })}
                />

                <Select
                  label="Type"
                  options={[
                    {label: 'Next Day', value: 'date'},
                    {label: 'Hours Ago', value: 'hours_ago'}
                  ]}
                  onChange={(val) => setEditing({ ...editing, trigger: { ...editing.trigger, type: val, value: val === 'date' ? 'next_day' : 5 } })}
                  value={editing.trigger.type}
                />

                {editing.trigger.type === 'hours_ago' ?
                  <TextField
                    label="Hours Ago"
                    type="number"
                    helpText={`Orders created within the last ${editing.trigger.value} hours since ${editing.trigger.hour}`}
                    min={1}
                    max={24}
                    value={`${editing.trigger.value}`}
                    onChange={(val) => setEditing({ ...editing, trigger: { ...editing.trigger, value: +val } })}
                  />
                : null}
              </FormLayout>
            : null}
          </Modal.Section>
        : null}
      </Modal>

      <Modal
        title="Recipient Modal"
        open={editing.recipient}
        onClose={() => setEditing(false)}
        secondaryActions={[{ content: 'Cancel', onAction: () => setEditing(false) }]}
        primaryAction={{content: 'Save', loading: saving, onAction: () => handleSaveRecipient(editing), disabled: saving }}>
        <Modal.Section>
          {editing.recipient ?
            <FormLayout>
              <TextField
                label="Email"
                type="email"
                value={newRecipientAddress}
                onChange={(val) => setNewRecipientAddress(val)}
                connectedRight={
                  <Button
                    disabled={!validEmail(newRecipientAddress)}
                    onClick={() => {
                    setEditing({
                      ...editing,
                      recipient: normalizeRecipient({
                        ...editing.recipient,
                        addresses: [...editing.recipient.addresses, newRecipientAddress]
                      })
                    });
                    setNewRecipientAddress('');
                  }}>
                    Add
                  </Button>}
              />

              <Stack spacing="tight">
                {editing.recipient.addresses.map(address =>
                  <Tag key={address} onRemove={() => {
                    setEditing({
                      ...editing,
                      recipient: normalizeRecipient({
                        ...editing.recipient,
                        addresses: editing.recipient.addresses.filter(tag => tag !== address)
                      })
                    })
                  }}>
                    {address}
                  </Tag>
                )}
              </Stack>


              <Select
                label="Filter Type"
                options={[
                  { label: 'Product Type', value: 'product_type' },
                  { label: 'Collection', value: 'collection' }
                ]}
                value={editing.recipient.type}
                onChange={(val) => {
                  setEditing({
                    ...editing,
                    recipient: normalizeRecipient({
                      ...editing.recipient,
                      type: val
                    })
                  });
                  setNewRecipientValue('');
                }}
              />


              <TextField
                label={editing.recipient.type === 'collection' ? 'Collection' : 'Product Type'}
                type="string"
                value={newRecipientValue}
                onChange={(val) => setNewRecipientValue(val)}
                connectedRight={
                  <Button
                    disabled={!newRecipientValue.trim()}
                    onClick={() => {
                      const trimmed = newRecipientValue.trim();
                      if (!trimmed) {
                        return;
                      }
                      const existing = editing.recipient.values || [];
                      const nextValues = existing.includes(trimmed) ? existing : [...existing, trimmed];
                      setEditing({
                        ...editing,
                        recipient: normalizeRecipient({
                          ...editing.recipient,
                          values: nextValues
                        })
                      });
                      setNewRecipientValue('');
                    }}
                  >
                    Add
                  </Button>}
              />

              <Stack spacing="tight">
                {editing.recipient.values.map(value =>
                  <Tag key={value} onRemove={() => {
                    setEditing({
                      ...editing,
                      recipient: normalizeRecipient({
                        ...editing.recipient,
                        values: editing.recipient.values.filter(existingValue => existingValue !== value)
                      })
                    })
                  }}>
                    {value}
                  </Tag>
                )}
              </Stack>
            </FormLayout>
          : null}
        </Modal.Section>
      </Modal>

      <Modal
        title="Email Preview"
        open={Boolean(previewModal?.open)}
        large
        onClose={handleClosePreview}
        secondaryActions={[{ content: 'Close', onAction: handleClosePreview }]}
      >
        {previewModal ? (() => {
          const summary = orderSummaryEmails[previewModal.summaryIndex] || {};
          const recipientOptions = (summary.recipients || []).map((recipient, idx) => ({
            label: recipientOptionLabel(recipient),
            value: `${idx}`
          }));
          const triggerOptions = (summary.triggers || []).map((trigger, idx) => ({
            label: triggerOptionLabel(trigger),
            value: `${idx}`
          }));

          return (
            <>
              <Modal.Section>
                <FormLayout>
                  <Select
                    label="Recipient"
                    options={recipientOptions}
                    placeholder="Select a recipient"
                    value={previewModal.recipientIndex ?? ''}
                    onChange={handlePreviewRecipientChange}
                    disabled={recipientOptions.length === 0}
                  />

                  {triggerOptions.length > 0 ? (
                    <Select
                      label="Trigger"
                      options={triggerOptions}
                      value={previewModal.triggerIndex ?? ''}
                      onChange={handlePreviewTriggerChange}
                    />
                  ) : null}

                  <TextField
                    label="Date (optional)"
                    placeholder="YYYY-MM-DD"
                    value={previewModal.date || ''}
                    onChange={handlePreviewDateChange}
                    autoComplete="off"
                  />

                  <Stack distribution="trailing">
                    <Button
                      onClick={() => loadPreview(previewModal)}
                      disabled={recipientOptions.length === 0}
                      loading={previewLoading}
                    >
                      Refresh preview
                    </Button>
                  </Stack>
                </FormLayout>
              </Modal.Section>

              <Modal.Section>
                {previewError ? (
                  <Banner status="critical">
                    {previewError}
                  </Banner>
                ) : null}

                {previewLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem 0' }}>
                    <Spinner accessibilityLabel="Loading preview" size="large" />
                  </div>
                ) : null}

                {!previewLoading && !previewError ? (
                  previewHtml ? (
                    <div
                      style={{
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        border: '1px solid #dfe3e8',
                        borderRadius: 4,
                        padding: '1rem',
                        background: '#ffffff'
                      }}
                      dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                  ) : (
                    <p>No preview content available.</p>
                  )
                ) : null}
              </Modal.Section>
            </>
          );
        })() : null}
      </Modal>
    </Layout.AnnotatedSection>
  )
}
